import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  CardActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import programmingContentService from "app/services/programming-content-service";
import useMetadata from "app/hooks/useMetadata";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Div from "@jumbo/shared/Div";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import VideoListAccordion from "components/VideoListAccordion";
import CodeSnippetList from "components/CodeSnippetList";
import FormActions from "components/FormActions";
import { ROUTES } from "app/utils/constants/routeConstants";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import ContentHeader from "components/ContentHeader/ContentHeader";
import {
  AccordionDetailsStyled,
  AccordionSummaryStyled,
  AccordionStyled,
} from "app/pages/components/mui/Accordions/StyledAccordion/StyleAccordion";
import { useSnackbar } from "notistack";

let Editor = () => <React.Fragment />;

const AdminProgrammingContentForm = () => {
  const { metadata } = useMetadata();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isUpdate = !!id;
  const hasFetchedRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  const languages = (metadata?.programmingLanguages || []).map(
    ({ key, value }) => ({
      key,
      value,
    })
  );

  const problemStates = (metadata?.problemStates || []).map(
    ({ key, value }) => ({
      key,
      value,
    })
  );

  const videoProviders = (metadata?.videoProviders || []).map(
    ({ key, value }) => ({
      key,
      value,
    })
  );

  // Memoize defaultFormData to prevent re-creation on every render
  const defaultFormData = useMemo(
    () => ({
      title: "",
      url: "",
      chapterName: "",
      description: "",
      language: "",
      status: "",
      contentDetails: [
        {
          title: "",
          content: "",
          active: true,
          problemCodeSnippets: [
            {
              codeSnippet: "",
              active: true,
              programmingLanguage: "",
              description: "",
            },
          ],
          problemVideos: [
            {
              videoLink: "",
              active: true,
              provider: "YOUTUBE",
              videoTitle: "",
              videoDescription: "",
            },
          ],
        },
      ],
    }),
    []
  );

  const [formData, setFormData] = useState(defaultFormData);
  const [editorStates, setEditorStates] = useState([EditorState.createEmpty()]);
  const [expandedCodeSnippetAccordions, setExpandedCodeSnippetAccordions] =
    useState({});
  const [expandedVideosAccordions, setExpandedVideosAccordions] = useState({});

  // Convert HTML content to EditorState
  const createEditorStateFromHtml = (html) => {
    if (!html) return EditorState.createEmpty();
    const blocksFromHtml = htmlToDraft(html);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
    return EditorState.createWithContent(contentState);
  };

  // Pre-populate form data when updating
  useEffect(() => {
    Editor = require("react-draft-wysiwyg").Editor;

    const initializeFormData = async () => {
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;

      try {
        let initialData = defaultFormData;
        let initialEditorStates = [EditorState.createEmpty()];

        if (isUpdate) {
          let fetchedContent = location.state?.content;
          if (!fetchedContent) {
            const response = await programmingContentService.getContentById(id);
            fetchedContent = response;
          }
          initialData = {
            ...fetchedContent,
            contentDetails: fetchedContent.contentDetails?.length
              ? fetchedContent.contentDetails.map((detail) => ({
                  ...detail,
                  problemCodeSnippets:
                    detail.problemCodeSnippets ||
                    defaultFormData.contentDetails[0].problemCodeSnippets,
                  problemVideos:
                    detail.problemVideos ||
                    defaultFormData.contentDetails[0].problemVideos,
                }))
              : defaultFormData.contentDetails,
          };
          initialEditorStates = fetchedContent.contentDetails?.map((detail) =>
            createEditorStateFromHtml(detail.content)
          ) || [EditorState.createEmpty()];
        }

        setFormData(initialData);
        setEditorStates(initialEditorStates);
      } catch (error) {
        enqueueSnackbar(`Failed to fetch content: ${error.message}`, {
          variant: "error",
        });
      }
    };

    initializeFormData();
  }, [isUpdate, location.state, id, defaultFormData, enqueueSnackbar]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddContentDetail = () => {
    setFormData((prev) => ({
      ...prev,
      contentDetails: [
        ...prev.contentDetails,
        {
          title: "",
          content: "",
          active: true,
          problemCodeSnippets: [
            {
              codeSnippet: "",
              active: true,
              programmingLanguage: "",
              description: "",
            },
          ],
          problemVideos: [
            {
              videoLink: "",
              active: true,
              provider: "",
              videoTitle: "",
              videoDescription: "",
            },
          ],
        },
      ],
    }));
    setEditorStates((prev) => [...prev, EditorState.createEmpty()]);
  };

  const handleRemoveContentDetail = (index) => {
    setFormData((prev) => ({
      ...prev,
      contentDetails: prev.contentDetails.filter((_, i) => i !== index),
    }));
    setEditorStates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddCodeSnippet = (contentIndex) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[contentIndex].problemCodeSnippets.push({
        codeSnippet: "",
        active: true,
        programmingLanguage: "",
        description: "",
      });
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleAddVideo = (contentIndex) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[contentIndex].problemVideos.push({
        videoLink: "",
        active: true,
        provider: "",
        videoTitle: "",
        videoDescription: "",
      });
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleRemoveCodeSnippet = (contentIndex, snippetIndex) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[contentIndex].problemCodeSnippets =
        updatedContentDetails[contentIndex].problemCodeSnippets.filter(
          (_, i) => i !== snippetIndex
        );
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleRemoveVideo = (contentIndex, videoIndex) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[contentIndex].problemVideos = updatedContentDetails[
        contentIndex
      ].problemVideos.filter((_, i) => i !== videoIndex);
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleContentDetailChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[index] = {
        ...updatedContentDetails[index],
        [field]: value,
      };
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleEditorChange = (index, editorState) => {
    setEditorStates((prev) => {
      const updatedEditorStates = [...prev];
      updatedEditorStates[index] = editorState;
      return updatedEditorStates;
    });
    const htmlContent = draftToHtml(
      convertToRaw(editorState.getCurrentContent())
    );
    handleContentDetailChange(index, "content", htmlContent);
  };

  const handleCodeSnippetChange = (
    contentIndex,
    snippetIndex,
    field,
    value
  ) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[contentIndex].problemCodeSnippets[snippetIndex] = {
        ...updatedContentDetails[contentIndex].problemCodeSnippets[
          snippetIndex
        ],
        [field]: value,
      };
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleVideoChange = (contentIndex, videoIndex, field, value) => {
    setFormData((prev) => {
      const updatedContentDetails = [...prev.contentDetails];
      updatedContentDetails[contentIndex].problemVideos[videoIndex] = {
        ...updatedContentDetails[contentIndex].problemVideos[videoIndex],
        [field]: value,
      };
      return {
        ...prev,
        contentDetails: updatedContentDetails,
      };
    });
  };

  const handleSave = async () => {
    try {
      let response;
      if (isUpdate && formData.id) {
        response = await programmingContentService.updateProgrammingContent(
          formData
        );
        enqueueSnackbar(
          `${formData.title} has been updated successfully.`,
          { variant: "success" }
        );
      } else {
        response = await programmingContentService.addProgrammingContent(
          formData
        );
        enqueueSnackbar(`${formData.title} added successfully!`, {
          variant: "success",
        });
      }
      setFormData(defaultFormData);
      setEditorStates([EditorState.createEmpty()]);
      const languageName =
        languages
          .find((lang) => lang.key === response.language)
          ?.value.toLowerCase() || "unknown";
      navigate(`${ROUTES.PROGRAMMING_CONTENT}/${languageName}/${response.url}`);
    } catch (error) {
      enqueueSnackbar(`Failed to save content: ${error.message}`, {
        variant: "error",
      });
    }
  };

  const handleCodeSnippetAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedCodeSnippetAccordions((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  const handleVideoAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedVideosAccordions((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  const handleAddContent = useCallback(() => {
    navigate(`${ROUTES.ADMIN_PROGRAMMING_CONTENT_ADD}`);
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAction = useCallback(
    (action) => {
      switch (action.id) {
        case "back":
          handleBack();
          break;
        case "add":
          handleAddContent();
          break;
        default:
          console.log("Unknown action:", action);
      }
    },
    [handleAddContent, handleBack]
  );

  return (
    <>
      <JumboCardQuick
        title={
          <ContentHeader
            isAdmin={true}
            onAction={handleAction}
            allowedActions={isUpdate ? ["add", "back"] : ["back"]}
            showLeftTabs={true}
          />
        }
        headerSx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiCardHeader-action": { my: -0.75 },
        }}
        wrapperSx={{ p: 0 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "& .MuiCardContent-root": {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
          },
          "& .MuiCardContent-root:last-child": {
            paddingBottom: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          <Box>
            <form>
              <AccordionStyled defaultExpanded>
                <AccordionSummaryStyled
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Typography variant="subtitle1">Content Metadata</Typography>
                </AccordionSummaryStyled>
                <AccordionDetailsStyled>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="URL"
                        name="url"
                        value={formData.url}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Chapter Name"
                        name="chapterName"
                        value={formData.chapterName}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Language</InputLabel>
                        <Select
                          name="language"
                          value={formData.language}
                          onChange={handleChange}
                          label="language"
                        >
                          {languages.map((lang) => (
                            <MenuItem key={lang.key} value={lang.key}>
                              {lang.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          label="Status"
                        >
                          {problemStates.map((state) => (
                            <MenuItem key={state.key} value={state.key}>
                              {state.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </AccordionDetailsStyled>
              </AccordionStyled>

              <Box>
                <AccordionStyled>
                  <AccordionSummaryStyled
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Typography variant="subtitle1">
                        Content Details
                      </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddContentDetail();
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </AccordionSummaryStyled>
                  <AccordionDetailsStyled>
                    {formData.contentDetails.map(
                      (contentDetail, contentIndex) => (
                        <Box
                          key={contentIndex}
                          sx={{
                            mb: 2,
                            p: 2,
                            border: "1px solid #ccc",
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            sx={{
                              mb: 2,
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <TextField
                              sx={{ width: "200px" }}
                              label="Content Title"
                              value={contentDetail.title}
                              onChange={(e) =>
                                handleContentDetailChange(
                                  contentIndex,
                                  "title",
                                  e.target.value
                                )
                              }
                              required
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={contentDetail.active}
                                  onChange={(e) =>
                                    handleContentDetailChange(
                                      contentIndex,
                                      "active",
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="Active"
                            />
                          </Box>
                          <Div
                            sx={{
                              flex: 1,
                              mb: 2,
                              border: "1px solid lightgray",
                            }}
                          >
                            <Editor
                              editorStyle={{
                                width: "100%",
                                minHeight: 100,
                                borderWidth: 1,
                                borderTopStyle: "solid",
                                borderColor: "lightgray",
                              }}
                              editorState={editorStates[contentIndex]}
                              onEditorStateChange={(editorState) =>
                                handleEditorChange(contentIndex, editorState)
                              }
                            />
                          </Div>

                          <AccordionStyled
                            sx={{ mt: 2, width: "100%" }}
                            onChange={handleCodeSnippetAccordionChange(
                              `codesnippet-${contentIndex}`
                            )}
                          >
                            <AccordionSummaryStyled
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  width: "100%",
                                }}
                              >
                                <Typography variant="subtitle1">
                                  Problem Code Snippets
                                </Typography>
                                {expandedCodeSnippetAccordions[
                                  `codesnippet-${contentIndex}`
                                ] && (
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddCodeSnippet(contentIndex);
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </AccordionSummaryStyled>
                            <AccordionDetailsStyled>
                              <CodeSnippetList
                                snippets={contentDetail.problemCodeSnippets}
                                onDeleteSnippet={(snippetIndex) =>
                                  handleRemoveCodeSnippet(
                                    contentIndex,
                                    snippetIndex
                                  )
                                }
                                onSnippetChange={(snippetIndex, field, value) =>
                                  handleCodeSnippetChange(
                                    contentIndex,
                                    snippetIndex,
                                    field,
                                    value
                                  )
                                }
                                programmingLanguages={languages}
                              />
                            </AccordionDetailsStyled>
                          </AccordionStyled>

                          <VideoListAccordion
                            title="Problem Videos"
                            videos={contentDetail.problemVideos}
                            onAddVideo={() => handleAddVideo(contentIndex)}
                            onDeleteVideo={(videoIndex) =>
                              handleRemoveVideo(contentIndex, videoIndex)
                            }
                            onVideoChange={(videoIndex, field, value) =>
                              handleVideoChange(
                                contentIndex,
                                videoIndex,
                                field,
                                value
                              )
                            }
                            expanded={
                              expandedVideosAccordions[
                                `video-${contentIndex}`
                              ] || false
                            }
                            onAccordionChange={handleVideoAccordionChange(
                              `video-${contentIndex}`
                            )}
                            videoProviders={videoProviders}
                            accordionKey={`video-${contentIndex}`}
                          />

                          <Box sx={{ mt: 2, textAlign: "right" }}>
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleRemoveContentDetail(contentIndex)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      )
                    )}
                  </AccordionDetailsStyled>
                </AccordionStyled>
              </Box>
            </form>
          </Box>
        </Box>
        <Divider />
        <CardActions
          sx={{
            py: (theme) => theme.spacing(1.5),
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <FormActions
            onCancel={() => {
              localStorage.removeItem("problemFormData");
              navigate(-1);
            }}
            onSave={handleSave}
            saveText="Add Content"
            isUpdate={isUpdate}
          />
        </CardActions>
      </JumboCardQuick>
    </>
  );
};

export default AdminProgrammingContentForm;
